import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TFunction } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useCampaignSendSize, useCampaignSendSizeData } from 'modules/campaigns/lib';
import { WinningCriteriaForm } from 'modules/experiments/components/pages/edit/sections/settings/WinningCriteriaForm';
import { WinningCriteriaTestDurationForm } from 'modules/experiments/components/pages/edit/sections/settings/WinningCriteriaTestDurationForm';
import { SendTimeForm } from 'modules/experiments/components/pages/edit/sections/settings/sendTime/SendTimeForm';
import { AudienceFormTriggerSubsetSetting } from 'modules/experiments/components/pages/edit/sections/settings/trigger/AudienceFormTriggerSubsetSetting';
import { Panel } from 'modules/experiments/components/ui';
import { PANEL_HEIGHT, dataTestId } from 'modules/experiments/constants';
import {
  getTakeoverCampaignId,
  getTakeoverCampaignLists,
  getTakeoverCampaignMessageMedium,
} from 'modules/experiments/ducks/selectors';
import {
  useExperimentsTranslations,
  useExperimentsUserPermission,
} from 'modules/experiments/hooks';

import { AudienceFormBlastSubsetSetting } from 'modules/experiments/components/pages/edit/sections/settings/blast/AudienceFormBlastSubsetSetting';
import { AudienceFormHoldoutSetting } from 'modules/experiments/components/pages/edit/sections/settings/blast/AudienceFormHoldoutSetting';
import {
  HeaderWrapper,
  SectionHeader,
  SettingsSectionDivider,
  TotalAudienceLabel,
  TotalAudienceValue,
  TotalAudienceWrapper,
} from 'modules/experiments/components/pages/edit/sections/settings/styled';
import { AudienceBreakdownChart } from 'modules/experiments/components/ui/audienceBreakdownChart';
import { UpdateExperimentSettingsFormFieldNames } from 'modules/experiments/constants/updateExperimentSettings';
import { useTakeoverExperimentContext } from 'modules/experiments/contexts';
import { hasOptimizedGroup, suffixNumberDisplay } from 'modules/experiments/utils';

import { ReadOnlySettings } from './readOnly';

type SettingsProps = {
  loading: boolean;
};

const AudienceHeaderDisplay = ({
  hasSendSize,
  t,
  sendSize,
}: {
  sendSize: number;
  hasSendSize: boolean;
  t: TFunction;
}) => {
  return (
    <HeaderWrapper data-test="audience-header-display-wrapper">
      <SectionHeader>{t('settings.audience')}</SectionHeader>
      {hasSendSize && (
        <TotalAudienceWrapper>
          <TotalAudienceLabel>
            {t('settings.audienceBreakdownChart.totalAudienceLabel')}&nbsp;
          </TotalAudienceLabel>
          <TotalAudienceValue>
            &nbsp;
            {`${suffixNumberDisplay(sendSize)} ${t(
              'settings.audienceBreakdownChart.totalAudienceValueSuffix',
            )}`}
          </TotalAudienceValue>
        </TotalAudienceWrapper>
      )}
    </HeaderWrapper>
  );
};

export const Settings = ({ loading }: SettingsProps) => {
  const { t } = useExperimentsTranslations();
  const campaignMessageMedium = useSelector(getTakeoverCampaignMessageMedium);
  const { viewOnlyPermission } = useExperimentsUserPermission(campaignMessageMedium);
  const {
    isBlastCampaign,
    isTriggerCampaign,
    isRunningExperiment,
    isInAppMessageMedium,
    isSendTimeExperiment,
    isSTOExperiment,
    unsavedChanges: { setUnsavedSettings },
  } = useTakeoverExperimentContext();
  const {
    formState: { isDirty },
  } = useFormContext();

  const campaignId = useSelector(getTakeoverCampaignId);
  const campaignLists = useSelector(getTakeoverCampaignLists);

  // Campaign State
  const { expectedSendSizeData, estimatedSendSizeData } = useCampaignSendSizeData(
    campaignId,
    campaignLists.length,
  );
  const { sendSize } = useCampaignSendSize({ estimatedSendSizeData, expectedSendSizeData });
  const hasSendSize = !!sendSize;

  // Update the experiment's context
  // when there is unsaved settings changes
  useEffect(() => {
    setUnsavedSettings(isDirty);
  }, [isDirty]);

  const { TEST_STRATEGY } = UpdateExperimentSettingsFormFieldNames;

  const [watchBlastTestSizeToggle] = useWatch({
    name: [TEST_STRATEGY],
  });
  let configurationOptions;

  if (isRunningExperiment || viewOnlyPermission) {
    configurationOptions = (
      <>
        <AudienceHeaderDisplay sendSize={sendSize ?? 0} hasSendSize={hasSendSize} t={t} />
        {isBlastCampaign && !isSTOExperiment && <AudienceBreakdownChart />}
        <ReadOnlySettings />
      </>
    );
  } else if (isSendTimeExperiment) {
    configurationOptions = (
      <>
        <AudienceHeaderDisplay sendSize={sendSize ?? 0} hasSendSize={hasSendSize} t={t} />
        <AudienceBreakdownChart />
        <SettingsSectionDivider />
        <WinningCriteriaForm />
        <SendTimeForm />
      </>
    );
  } else if (isSTOExperiment) {
    configurationOptions = (
      <>
        <AudienceHeaderDisplay sendSize={sendSize ?? 0} hasSendSize={hasSendSize} t={t} />
        <AudienceBreakdownChart />
        <SettingsSectionDivider />
        <WinningCriteriaForm />
      </>
    );
  } else if (isBlastCampaign) {
    configurationOptions = (
      <>
        <AudienceHeaderDisplay sendSize={sendSize ?? 0} hasSendSize={hasSendSize} t={t} />
        <AudienceBreakdownChart />
        <SettingsSectionDivider />
        <AudienceFormHoldoutSetting />
        {!isInAppMessageMedium && (
          <>
            <SettingsSectionDivider />
            <AudienceFormBlastSubsetSetting />
          </>
        )}
        <WinningCriteriaForm />
        {hasOptimizedGroup(watchBlastTestSizeToggle) && <WinningCriteriaTestDurationForm />}
      </>
    );
  } else if (isTriggerCampaign) {
    configurationOptions = (
      <>
        {!isInAppMessageMedium && (
          <>
            <AudienceHeaderDisplay sendSize={sendSize ?? 0} hasSendSize={hasSendSize} t={t} />
            <AudienceFormTriggerSubsetSetting />
          </>
        )}
        <WinningCriteriaForm isFirstSection={isInAppMessageMedium} />
      </>
    );
  }

  return (
    <Panel
      testId={dataTestId.settingsSection}
      title={t('panelHeading.settings')}
      height={PANEL_HEIGHT.SETTINGS}
      loading={loading}
    >
      {configurationOptions}
    </Panel>
  );
};
